import { issuePathById, newHireJourneyPathById } from "helpers/url/pages/v1";
import { Link as RouterLink, useNavigate } from "react-router";
import { NewHireJourneyId } from "models/newHire";
import { BrandTag, GrayTag, RedTag } from "components/Tag/ColorOutineTag";
import { ActionSummary, ActionV2 } from "features/Resources/Shared/models";
import { extractAction, isActionResolved } from "features/Resources/Shared/helpers";
import { actionPathByPrefixId } from "helpers/url/pages/v2/actionPaths";
import { useActionIssuesResolvedQuery, useIssuesByNewHireIdQuery } from "../hooks";

interface NumberOfIssuesByNewHireTagProps {
  newHireId: NewHireJourneyId;
}

export function NumberOfIssuesByNewHireTag({ newHireId }: NumberOfIssuesByNewHireTagProps) {
  const { data: issues } = useIssuesByNewHireIdQuery(newHireId);
  const navigate = useNavigate();

  if (!newHireId || !issues) {
    return null;
  }
  const singular = issues?.length === 1 ? "" : "s";
  return !issues?.length ? (
    <GrayTag>No Issues</GrayTag>
  ) : (
    <RedTag
      cursor="pointer"
      onClick={() => navigate(issues?.length ? issuePathById(issues[0]?.id) : newHireJourneyPathById(newHireId))}
    >
      {`${issues?.length} issue${singular}`}
    </RedTag>
  );
}

interface ShowIssuesOrReadyByIdTagProps {
  action: ActionSummary | ActionV2 | undefined;
}

export function ShowIssuesOrReadyByIdTag({ action }: ShowIssuesOrReadyByIdTagProps) {
  const resolvedAction = extractAction(action);
  const { data, isFetching } = useActionIssuesResolvedQuery(resolvedAction?.prefixId);
  if (isActionResolved(resolvedAction)) {
    return null;
  }

  if (resolvedAction?.id === undefined || isFetching || !data) {
    return <GrayTag>Loading...</GrayTag>;
  }

  const count = data.nonTiming.unresolvedCount;
  const isReadyToSend = data.nonTiming.resolved;

  return isReadyToSend ? (
    <BrandTag>Ready</BrandTag>
  ) : (
    <RouterLink to={actionPathByPrefixId(resolvedAction?.prefixId)}>
      <RedTag>{`${count} Issue${count === 1 ? "" : "s"}`}</RedTag>
    </RouterLink>
  );
}

interface ShowActionIssuesTagProps {
  action: ActionSummary | ActionV2 | undefined;
}

export function ShowActionIssuesTag({ action }: ShowActionIssuesTagProps) {
  const resolvedAction = extractAction(action);
  const { data, isFetching } = useActionIssuesResolvedQuery(resolvedAction?.prefixId);
  if (isActionResolved(resolvedAction)) {
    return null;
  }

  if (resolvedAction?.id === undefined || isFetching) {
    return <GrayTag>Loading...</GrayTag>;
  }

  const unresolvedCount = data?.nonTiming?.unresolvedCount ?? 0;

  return unresolvedCount === 0 ? (
    null
  ) : (
    <RouterLink to={actionPathByPrefixId(resolvedAction?.prefixId)}>
      <RedTag>{`${unresolvedCount} Issue${unresolvedCount === 1 ? "" : "s"}`}</RedTag>
    </RouterLink>
  );
}
